import useTranslation from "next-translate/useTranslation"
import styled from "styled-components"
import NiceModal from "@ebay/nice-modal-react"
import Modal from "@hornet-web-react/core/components/Modals/Modal"
import TextButton from "@hornet-web-react/core/components/UI/TextButton"
import { FormEvent, useState } from "react"
import { Fieldset } from "@hornet-web-react/core/components/Form"
import InteractiveComponentWrapper from "@hornet-web-react/core/components/Utils/InteractiveComponentWrapper"
import ToggleV3 from "@hornet-web-react/core/components/Form/ToggleV3"
import Trans from "next-translate/Trans"
import { useCookieConsent } from "@hornet-web-react/core/hooks/use-cookie-consent"
import { useLazyLoaded } from "@hornet-web-react/core/hooks/use-lazy-loaded"

const Toggles = styled(InteractiveComponentWrapper)``

const i18nKey = "hornet:components.modals.privacy_manager_modal"

const PrivacyManagerModalContent = styled.div`
  ${({ theme }) => theme.font.regular.subheadline};
`

type PrivacyManagerFormData = {
  enablePersonalizedAds: boolean
  enableTrafficMetrics: boolean
}

const StyledModal = styled(Modal)`
  min-width: 0;
  max-width: ${({ theme }) => theme.width.containerMaxWidthMobile};
`

const ModalTitle = styled.h2`
  font-size: 28px;
  line-height: 1.25;
  margin-top: 0;
  margin-bottom: 12px;
  color: #1d1d1d;
  font-weight: 500;
`
const ModalText = styled.p`
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  margin-top: 0;
  margin-bottom: 16px;
  color: #757575;
`
const ModalTextUl = styled.ul`
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  margin-top: 0;
  margin-bottom: 16px;
  color: #757575;
`

const PrivacyManagerModal = NiceModal.create(() => {
  const { t } = useTranslation()
  const { updateConsent } = useCookieConsent()
  const modal = NiceModal.useModal()
  const { get: getGeoLocationInfoModal } = useLazyLoaded(
    () => import("../Modals/GeoLocationInfoModal")
  )

  const [formData, setFormData] = useState<PrivacyManagerFormData>({
    enablePersonalizedAds: true,
    enableTrafficMetrics: true,
  })

  const handleRejectAllClick = () => {
    setFormData({ enablePersonalizedAds: false, enableTrafficMetrics: false })
    updateConsent(false, false)
    modal.resolve()
    modal.hide()
  }

  const handleAcceptChoiceClick = () => {
    updateConsent(formData.enablePersonalizedAds, formData.enableTrafficMetrics)
    modal.resolve()
    modal.hide()
  }

  const handleGeoLocationInfoModalClick = () =>
    getGeoLocationInfoModal((modal) => NiceModal.show(modal, {}))

  const handleInputChange = (value, name) => {
    setFormData({
      ...formData,
      [name]: value,
    })
  }

  return (
    <StyledModal
      open={modal.visible}
      locked={false}
      onClose={modal.hide}
      isFullscreen={true}
      hasCloseButton={true}
      actions={
        <>
          <TextButton
            size={"small"}
            primary={false}
            onClick={handleRejectAllClick}
          >
            {t(`${i18nKey}.button_reject_all`)}
          </TextButton>
          <TextButton
            size={"small"}
            primary={true}
            onClick={handleAcceptChoiceClick}
          >
            {t(`${i18nKey}.button_accept`)}
          </TextButton>
        </>
      }
    >
      <PrivacyManagerModalContent>
        <ModalTitle>{t(`${i18nKey}.title`)}</ModalTitle>

        <ModalText>{t(`${i18nKey}.text_1`)}</ModalText>

        <ModalTextUl>
          <li>{t(`${i18nKey}.cookie_type_list_item_1`)}</li>
          <li>{t(`${i18nKey}.cookie_type_list_item_2`)}</li>
        </ModalTextUl>

        <ModalText>{t(`${i18nKey}.text_2`)}</ModalText>

        <form onSubmit={(event: FormEvent) => event.preventDefault()}>
          <Fieldset style={{ marginBottom: "20px" }}>
            <Toggles isInteractive={true}>
              <ToggleV3
                label={t(`${i18nKey}.label_toggle_1`)}
                name={"enablePersonalizedAds"}
                value={formData.enablePersonalizedAds}
                onChange={handleInputChange}
              />
              <ToggleV3
                label={t(`${i18nKey}.label_toggle_2`)}
                name={"enableTrafficMetrics"}
                value={formData.enableTrafficMetrics}
                onChange={handleInputChange}
              />
            </Toggles>
          </Fieldset>
        </form>

        <ModalText onClick={handleGeoLocationInfoModalClick}>
          <Trans
            i18nKey={`${i18nKey}.text_3`}
            /* eslint-disable-next-line react/jsx-key */
            components={[<a href="#" />]}
          />
        </ModalText>

        <ModalText>
          <Trans
            i18nKey={`${i18nKey}.text_4`}
            components={[
              /* eslint-disable-next-line react/jsx-key */
              <a
                href="https://hornetapp.com/about/privacy-policy"
                target="_blank"
              />,
            ]}
          />
        </ModalText>
      </PrivacyManagerModalContent>
    </StyledModal>
  )
})

export default PrivacyManagerModal
